import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

// /review?t=<token>&r=<1..5> — public, no login. The token from the
// delivered email is the only credential. Two questions, nothing else:
// a star rating and an optional one-liner.
//
// The Google review link is shown to EVERYONE, whatever the rating.
//
// ⛔ Ne jamais le remettre derrière une condition sur la note. Jusqu'au
// 2026-11-04 il n'apparaissait qu'à 5★ : c'est du review gating, interdit
// par les règles de Google sur les fiches d'établissement — sanction
// possible jusqu'à la suppression de la fiche — et par la FTC,
// 16 CFR Part 465, qui interdit de supprimer ou détourner les avis
// négatifs. Le test « même lien à 1★ qu'à 5★ » garde cette ligne.
//
// Une note basse ajoute une voie de réponse directe, elle n'en retire
// aucune : en plus du lien, jamais à la place.

const NAVY = '#0B2038'
const AQUA = '#1FC0D4'
const INK2 = '#5B6B7D'
const LINE = '#E5E7EB'
const GOLD = '#D4A017'
const GOOGLE_REVIEW_URL = import.meta.env.VITE_GOOGLE_REVIEW_URL || ''

export default function Review() {
  const [params] = useSearchParams()
  const token = params.get('t') || ''
  const presetRating = Number(params.get('r')) || 0

  const [loading, setLoading] = useState(true)
  const [expired, setExpired] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [rating, setRating] = useState(presetRating >= 1 && presetRating <= 5 ? presetRating : 0)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(null) // { rating, alreadyThere }

  useEffect(() => {
    if (!token) { setExpired(true); setLoading(false); return }
    let alive = true
    ;(async () => {
      try {
        const res = await fetch(`/.netlify/functions/submit-review?t=${encodeURIComponent(token)}`)
        const data = await res.json().catch(() => ({}))
        if (!alive) return
        if (!res.ok) { setExpired(true) }
        else {
          setFirstName(data.first_name || '')
          if (data.submitted) setDone({ rating: data.rating, alreadyThere: true })
        }
      } catch {
        if (alive) setExpired(true)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [token])

  async function submit(e) {
    e.preventDefault()
    if (!rating) { setError('Tap a star first.'); return }
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/.netlify/functions/submit-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, rating, comment }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.status === 409) { setDone({ rating: data.rating, alreadyThere: true }); return }
      if (!res.ok) { setError(data?.error || 'Could not send. Try again.'); return }
      setDone({ rating: data.rating, alreadyThere: false })
    } catch {
      setError('Network error. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <Frame><p style={{ color: INK2, fontSize: 14 }}>Loading…</p></Frame>

  if (expired) {
    return (
      <Frame>
        <h1 style={h1}>This link has expired.</h1>
        <p style={{ color: INK2, fontSize: 14, marginTop: 8 }}>
          It may already have been used. Thanks for your order all the same 👻
        </p>
        <a href="https://ghostlaundry.com" style={{ ...btnNavy, marginTop: 20 }}>Back to Ghost Laundry</a>
      </Frame>
    )
  }

  if (done) return <ThankYou rating={done.rating} alreadyThere={done.alreadyThere} />

  return (
    <Frame>
      <h1 style={h1}>
        Thanks for your order{firstName ? `, ${firstName}` : ''} 👻
      </h1>
      <p style={{ color: INK2, fontSize: 14, margin: '6px 0 0' }}>How did we do?</p>

      <form onSubmit={submit} style={{ marginTop: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              aria-label={`${n} star${n > 1 ? 's' : ''}`}
              aria-pressed={rating === n}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 34, lineHeight: 1, padding: 4,
                filter: n <= rating ? 'none' : 'grayscale(1)',
                opacity: n <= rating ? 1 : 0.35,
                transition: 'opacity 120ms ease',
              }}
            >
              ⭐
            </button>
          ))}
        </div>

        <label htmlFor="c" style={{ display: 'block', marginTop: 20, fontSize: 13, fontWeight: 700, color: NAVY }}>
          Anything you&apos;d like to add?
        </label>
        <textarea
          id="c"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={280}
          rows={3}
          placeholder="One line about your experience (optional)"
          style={{
            width: '100%', marginTop: 6, padding: '10px 12px', fontSize: 14,
            border: `1.5px solid ${LINE}`, borderRadius: 10, resize: 'none',
            fontFamily: 'inherit', color: NAVY, boxSizing: 'border-box',
          }}
        />
        <div style={{ textAlign: 'right', fontSize: 11, color: INK2 }}>{comment.length}/280</div>

        {error && (
          <p style={{ background: '#FEE2E2', color: '#B91C1C', fontSize: 13, fontWeight: 700, padding: '8px 12px', borderRadius: 8, margin: '10px 0 0' }}>
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting} style={{ ...btnNavy, width: '100%', marginTop: 16, opacity: submitting ? 0.6 : 1 }}>
          {submitting ? 'Sending…' : 'Send'}
        </button>
      </form>
    </Frame>
  )
}

function ThankYou({ rating, alreadyThere }) {
  const five = rating === 5
  const four = rating === 4
  const low = rating <= 3
  return (
    <Frame>
      <div style={{ fontSize: 40, lineHeight: 1 }}>{low ? '👻' : '🤍'}</div>
      <h1 style={{ ...h1, marginTop: 10 }}>
        {alreadyThere
          ? 'You already left us a review — thank you!'
          : five
            ? 'You made our day 🤍'
            : four
              ? 'Thanks! Your review is now live on our site 👻'
              : "Thanks for the honesty. We'll make it right."}
      </h1>

      {rating != null && (
        <div style={{ marginTop: 10, fontSize: 22, color: GOLD, letterSpacing: 2 }}>
          {'⭐'.repeat(rating || 0)}
        </div>
      )}

      {/* Voie de réponse directe pour une note basse. EN PLUS du lien
          Google ci-dessous, jamais à la place. */}
      {low && !alreadyThere && (
        <p style={{ color: INK2, fontSize: 14, marginTop: 10 }}>
          Someone from Ghost will reach out — or just reply to the email,
          Sébastien reads these himself.
        </p>
      )}

      {/* ⛔ Pas de condition sur `rating` ici. Voir l'en-tête du fichier :
          n'afficher ce lien qu'aux bonnes notes est du review gating. Le
          sous-titre est neutre exprès — pousser « ça aide une petite
          entreprise » devant un client à une étoile serait une pression
          déplacée. */}
      {GOOGLE_REVIEW_URL && (
        <>
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener"
            style={{ ...btnAqua, width: '100%', marginTop: 20 }}
          >
            Leave a review on Google
          </a>
          <p style={{ color: INK2, fontSize: 12, marginTop: 8 }}>
            Reviews on Google help other people find us.
          </p>
        </>
      )}

      <a href="https://ghostlaundry.com" style={{ display: 'inline-block', marginTop: 22, fontSize: 13, fontWeight: 700, color: AQUA, textDecoration: 'none' }}>
        Back to Ghost Laundry →
      </a>
    </Frame>
  )
}

function Frame({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 420, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 18, padding: 26, textAlign: 'center' }}>
        <img src="/logo-ghost.png" alt="" aria-hidden="true" width="56" height="56" style={{ width: 56, height: 56, borderRadius: 14, marginBottom: 14 }} />
        {children}
      </div>
    </div>
  )
}

const h1 = {
  fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
  fontSize: 21, fontWeight: 800, color: NAVY, margin: 0, lineHeight: 1.25,
}
const btnNavy = {
  display: 'inline-block', background: NAVY, color: '#fff', padding: '13px 22px',
  borderRadius: 12, fontWeight: 800, fontSize: 14, textDecoration: 'none',
  border: 'none', cursor: 'pointer', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
}
const btnAqua = { ...btnNavy, background: AQUA, color: NAVY }
